import { Signer } from '@ethersproject/abstract-signer'
import { Event } from '@ethersproject/contracts'
import { Provider } from '@ethersproject/providers'
import { EthAddress } from '@xylabs/sdk-js'
import { XyoWorldMinterVersion2, XyoWorldMinterVersion2__factory as XyoWorldMinterVersion2Factory } from '@xyo-network/world-typechain'

import { Quadkey } from '../Quadkey'
import { XyoErc20Wrapper } from './Erc20'
import { XyoEthersWrapperBase } from './EthersBase'
import { XyoWorldGeotokensWrapper } from './Geotokens'

class XyoWorldMinterVersion2Wrapper extends XyoEthersWrapperBase {
  public contract: XyoWorldMinterVersion2

  constructor(address: EthAddress, provider: Provider, signer?: Signer) {
    super(provider, signer)
    this.contract = XyoWorldMinterVersion2Factory.connect(address.toString(), this.signer ?? this.provider)
  }

  public async getAllEventsByQuadkey(quadkey: Quadkey) {
    const allEvents: Event[] = []
    const started = await this.getAuctionStartedByQuadkey(quadkey)
    const bids = await this.getBidPlacedByQuadkey(quadkey)
    const refunds = await this.getRefundedCurrentBidByQuadkey(quadkey)
    const mintingFeeSet = await this.getMintingFeeSentByQuadkey(quadkey)
    const tokensBurned = await this.getTokensBurnedByQuadkey(quadkey)
    const tokensFlowed = await this.getTokensFlowedByQuadkey(quadkey)
    const geotokenMinted = await this.getGeotokenMintedByQuadkey(quadkey)
    return allEvents
      .concat(started ?? [])
      .concat(bids ?? [])
      .concat(refunds ?? [])
      .concat(mintingFeeSet ?? [])
      .concat(tokensFlowed ?? [])
      .concat(tokensBurned ?? [])
      .concat(geotokenMinted ?? [])
      .sort((n1: Event, n2: Event) => {
        if (n1.blockNumber < n2.blockNumber) {
          return -1
        }
        if (n1.blockNumber > n2.blockNumber) {
          return 1
        }
        return 0
      })
  }

  public async getAuctionStartedByQuadkey(quadkey: Quadkey) {
    const filter = this.contract.filters.AuctionStarted(null, quadkey.toHex(), null, null)
    return await this.contract.queryFilter(filter)
  }

  public async getBidPlaced() {
    const filter = this.contract.filters.BidPlaced(null, null, null)
    return await this.contract.queryFilter(filter)
  }

  public async getBidPlacedByBidder(bidder: EthAddress) {
    const filter = this.contract.filters.BidPlaced(bidder.toString(), null, null)
    return await this.contract.queryFilter(filter)
  }

  public async getBidPlacedByQuadkey(quadkey: Quadkey) {
    const filter = this.contract.filters.BidPlaced(null, quadkey.toHex(), null)
    return await this.contract.queryFilter(filter)
  }

  public async getCurrency() {
    const currencyAddress = EthAddress.fromString(await this.contract.currency())
    return currencyAddress ? new XyoErc20Wrapper(currencyAddress, this.provider, this.signer) : undefined
  }

  public async getGeotokenMintedByQuadkey(quadkey: Quadkey) {
    const filter = this.contract.filters.GeotokenMinted(null, quadkey.toHex())
    return await this.contract.queryFilter(filter)
  }

  public async getMintingFeeSent() {
    const filter = this.contract.filters.MintingFeeSent(null, null, null)
    return await this.contract.queryFilter(filter)
  }

  public async getMintingFeeSentByQuadkey(quadkey: Quadkey) {
    const filter = this.contract.filters.MintingFeeSent(null, quadkey.toHex(), null)
    return await this.contract.queryFilter(filter)
  }

  public async getNow() {
    return await this.contract.getNow()
  }

  public async getRefundedCurrentBidByQuadkey(quadkey: Quadkey) {
    const filter = this.contract.filters.RefundedCurrentBid(null, quadkey.toHex(), null)
    return await this.contract.queryFilter(filter)
  }

  public async getTokens() {
    const currencyAddress = EthAddress.fromString(await this.contract.tokens())
    if (currencyAddress) {
      return new XyoWorldGeotokensWrapper(currencyAddress, this.provider, this.signer)
    }
  }

  public async getTokensBurned() {
    const filter = this.contract.filters.TokensBurned(null, null)
    return await this.contract.queryFilter(filter)
  }

  public async getTokensBurnedByQuadkey(quadkey: Quadkey) {
    const filter = this.contract.filters.TokensBurned(quadkey.toHex(), null)
    return await this.contract.queryFilter(filter)
  }

  public async getTokensFlowed() {
    const filter = this.contract.filters.TokensFlowed(null, null, null)
    return await this.contract.queryFilter(filter)
  }

  public async getTokensFlowedByQuadkey(quadkey: Quadkey) {
    const filterFrom = this.contract.filters.TokensFlowed(null, quadkey.toHex(), null)
    const from = await this.contract.queryFilter(filterFrom)
    const filterTo = this.contract.filters.TokensFlowed(quadkey.toHex(), null, null)
    const to = await this.contract.queryFilter(filterTo)
    return from.concat(to)
  }
}

export { XyoWorldMinterVersion2Wrapper }
